import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from './Dialog';
import { cn } from './uiTheme';

type ScrollableModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  /** Rendered in a footer that stays pinned below the scrollable body (e.g. Cancel/Confirm buttons). */
  footer?: React.ReactNode;
  disableClose?: boolean;
  contentClassName?: string;
  bodyClassName?: string;
};

/**
 * Same API as Modal, but the header and footer stay fixed while only the body between
 * them scrolls — for content that may be taller than the viewport allows.
 */
export function ScrollableModal({
  isOpen,
  onClose,
  title,
  icon,
  children,
  footer,
  disableClose = false,
  contentClassName,
  bodyClassName = 'px-6 py-5',
}: ScrollableModalProps) {
  const handleOpenChange = (open: boolean) => {
    if (!open && !disableClose) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        open={isOpen}
        showCloseButton={!disableClose}
        contentClassName={cn('overflow-hidden', contentClassName)}
        aria-describedby={undefined}
        onEscapeKeyDown={(e: KeyboardEvent) => disableClose && e.preventDefault()}
        onPointerDownOutside={(e: Event) => disableClose && e.preventDefault()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle asChild>
            <h2 className="flex items-center pr-10 text-xl font-semibold text-slate-100">
              {icon && <span className="mr-2 flex-shrink-0">{icon}</span>}
              {title}
            </h2>
          </DialogTitle>
        </DialogHeader>
        <div className={cn('min-h-0 flex-1 overflow-y-auto', bodyClassName)}>{children}</div>
        {footer ? <DialogFooter className="shrink-0">{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}

export default ScrollableModal;
